import { API_BASE } from '@/shared/services/api';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Search,
  X,
  Package,
  Truck,
  MapPin,
  User,
  FileText,
  Clock,
  ArrowRight,
  Loader2,
  Command } from
'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle } from
'@/shared/components/ui/dialog';
import {
  Command as CommandPrimitive,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator } from
'@/shared/components/ui/command';

const API_URL = API_BASE;

// Entity type configurations
const ENTITY_CONFIG = {
  ORDER: { icon: FileText, color: 'text-blue-500', route: '/wms/trails' },
  ROUTE: { icon: Truck, color: 'text-green-500', route: '/wms/trails' },
  SHIPMENT: { icon: Package, color: 'text-purple-500', route: '/wms/trails' },
  VEHICLE: { icon: Truck, color: 'text-orange-500', route: '/wms/geofencing' },
  ZONE: { icon: MapPin, color: 'text-red-500', route: '/wms/geofencing' },
  USER: { icon: User, color: 'text-slate-500', route: '/wms/trails' }
};

// Quick actions
const QUICK_ACTIONS = [
{ label: 'View Geofencing Map', route: '/wms/geofencing', icon: MapPin },
{ label: 'Trail Dashboard', route: '/wms/trails', icon: FileText },
{ label: 'Audit Trail', route: '/wms/trails/audit', icon: Clock },
{ label: 'Scan Trail', route: '/wms/trails/scan', icon: Package },
{ label: 'Mobile Receiving', route: '/mobile/receiving', icon: Package },
{ label: 'Driver Dashboard', route: '/driver', icon: Truck }];


export const GlobalSearch = ({ className }) => {
  const { t } = useTranslation('navigation');
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const searchTimeoutRef = useRef(null);
  const navigate = useNavigate();

  // Load recent searches from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('recentSearches');
    if (stored) {
      try {
        setRecentSearches(JSON.parse(stored).slice(0, 5));
      } catch (e) {
        setRecentSearches([]);
      }
    }
  }, []);

  // Keyboard shortcut to open search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Search function
  const performSearch = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults([]);
      return;
    }

    setIsLoading(true);
    try {
      const token = localStorage.getItem('ezerpToken');
      if (!token) return;

      // Search trail events
      const response = await fetch(
        `${API_URL}/api/trails/events/query?limit=10`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();

        // Filter results that match the query
        const filtered = (data.events || []).filter((event) => {
          const searchLower = searchQuery.toLowerCase();
          return (
            event.entity_id?.toLowerCase().includes(searchLower) ||
            event.event_type?.toLowerCase().includes(searchLower) ||
            event.actor?.id?.toLowerCase().includes(searchLower) ||
            JSON.stringify(event.payload).toLowerCase().includes(searchLower));

        }).slice(0, 8);

        setResults(filtered.map((event) => ({
          type: 'event',
          id: event.id,
          title: event.entity_id || event.event_type,
          subtitle: event.event_type,
          entityType: event.entity_type,
          timestamp: event.timestamp_server
        })));
      }

      // Also search geofencing zones
      const zonesResponse = await fetch(
        `${API_URL}/api/geofencing/zones`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (zonesResponse.ok) {
        const zonesData = await zonesResponse.json();
        const matchingZones = (zonesData.zones || []).filter((zone) =>
        zone.name.toLowerCase().includes(searchQuery.toLowerCase())
        ).slice(0, 3);

        setResults((prev) => [
        ...prev,
        ...matchingZones.map((zone) => ({
          type: 'zone',
          id: zone.id,
          title: zone.name,
          subtitle: `${zone.category} zone`,
          entityType: 'ZONE'
        }))]
        );
      }

      // Search vehicles
      const vehiclesResponse = await fetch(
        `${API_URL}/api/geofencing/vehicles`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (vehiclesResponse.ok) {
        const vehiclesData = await vehiclesResponse.json();
        const matchingVehicles = (vehiclesData.vehicles || []).filter((v) =>
        v.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        v.vehicleId.toLowerCase().includes(searchQuery.toLowerCase())
        ).slice(0, 3);

        setResults((prev) => [
        ...prev,
        ...matchingVehicles.map((v) => ({
          type: 'vehicle',
          id: v.id,
          title: v.name,
          subtitle: `${v.status} - ${v.driverName || 'No driver'}`,
          entityType: 'VEHICLE'
        }))]
        );
      }

    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      performSearch(query);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [query, performSearch]);

  // Handle result selection
  const handleSelect = (result) => {
    // Save to recent searches
    const newRecent = [
    { query: result.title, entityType: result.entityType, timestamp: Date.now() },
    ...recentSearches.filter((r) => r.query !== result.title)].
    slice(0, 5);
    setRecentSearches(newRecent);
    localStorage.setItem('recentSearches', JSON.stringify(newRecent));

    // Navigate
    const config = ENTITY_CONFIG[result.entityType] || ENTITY_CONFIG.ORDER;
    navigate(`${config.route}?entity=${result.entityType}&id=${result.id}`);
    setIsOpen(false);
    setQuery('');
  };

  // Handle quick action
  const handleQuickAction = (action) => {
    navigate(action.route);
    setIsOpen(false);
    setQuery('');
  };

  return (
    <>
      {/* Trigger Button */}
      <div className={`relative ${className}`}>
        <Button
          variant="outline"
          className="w-full justify-start text-muted-foreground bg-muted/50 border-transparent hover:bg-background hover:border-border"
          onClick={() => setIsOpen(true)}
          data-testid="global-search-trigger">
          
          <Search className="h-4 w-4 mr-2" />
          <span className="hidden sm:inline">{t('searchPlaceholder')}</span>
          <span className="sm:hidden">{t('search')}</span>
          <kbd className="ml-auto pointer-events-none hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
            <span className="text-xs">⌘</span>K
          </kbd>
        </Button>
      </div>

      {/* Search Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="p-0 max-w-2xl" data-testid="global-search-dialog">
          <CommandPrimitive className="rounded-lg border shadow-md">
            <div className="flex items-center border-b px-3">
              <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
              <input
                placeholder="Search shipments, routes, zones, vehicles..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="flex h-12 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                autoFocus
                data-testid="global-search-input" />
              
              {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              {query &&
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setQuery('')}>
                
                  <X className="h-4 w-4" />
                </Button>
              }
            </div>

            <CommandList className="max-h-[400px] overflow-y-auto">
              {/* Loading state */}
              {isLoading && query.length >= 2 &&
              <div className="py-6 text-center text-sm text-muted-foreground">{t("searching", "Searching...")}

              </div>
              }

              {/* No results */}
              {!isLoading && query.length >= 2 && results.length === 0 &&
              <div className="py-6 text-center text-sm text-muted-foreground">{t("noResultsFound", "No results found for \"")}
                {query}"
                </div>
              }

              {/* Search Results */}
              {results.length > 0 &&
              <CommandGroup heading="Results">
                  {results.map((result) => {
                  const config = ENTITY_CONFIG[result.entityType] || ENTITY_CONFIG.ORDER;
                  const Icon = config.icon;

                  return (
                    <CommandItem
                      key={`${result.type}-${result.id}`}
                      onSelect={() => handleSelect(result)}
                      className="cursor-pointer"
                      data-testid={`search-result-${result.id}`}>
                      
                        <div className={`p-1.5 rounded mr-3 bg-muted ${config.color}`}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{result.title}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {result.subtitle}
                          </p>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {result.entityType}
                        </Badge>
                        <ArrowRight className="h-4 w-4 text-muted-foreground ml-2" />
                      </CommandItem>);

                })}
                </CommandGroup>
              }

              {/* Recent Searches */}
              {!query && recentSearches.length > 0 &&
              <CommandGroup heading="Recent">
                  {recentSearches.map((search, idx) => {
                  const config = ENTITY_CONFIG[search.entityType] || ENTITY_CONFIG.ORDER;
                  const Icon = config.icon;

                  return (
                    <CommandItem
                      key={`recent-${idx}`}
                      onSelect={() => setQuery(search.query)}
                      className="cursor-pointer">
                      
                        <Clock className="h-4 w-4 mr-3 text-muted-foreground" />
                        <span>{search.query}</span>
                        <Badge variant="outline" className="ml-auto text-xs">
                          {search.entityType}
                        </Badge>
                      </CommandItem>);

                })}
                </CommandGroup>
              }

              <CommandSeparator />

              {/* Quick Actions */}
              <CommandGroup heading="Quick Actions">
                {QUICK_ACTIONS.map((action) => {
                  const Icon = action.icon;
                  return (
                    <CommandItem
                      key={action.route}
                      onSelect={() => handleQuickAction(action)}
                      className="cursor-pointer">
                      
                      <Icon className="h-4 w-4 mr-3 text-muted-foreground" />
                      <span>{action.label}</span>
                      <ArrowRight className="h-4 w-4 text-muted-foreground ml-auto" />
                    </CommandItem>);

                })}
              </CommandGroup>
            </CommandList>

            <div className="border-t px-3 py-2 text-xs text-muted-foreground flex items-center justify-between">
              <div className="flex items-center gap-2">
                <kbd className="px-1.5 py-0.5 rounded bg-muted">{t("key_8351", "\u2191\u2193")}</kbd>
                <span>{t("navigate", "Navigate")}</span>
              </div>
              <div className="flex items-center gap-2">
                <kbd className="px-1.5 py-0.5 rounded bg-muted">{t("enter", "Enter")}</kbd>
                <span>{t("select", "Select")}</span>
              </div>
              <div className="flex items-center gap-2">
                <kbd className="px-1.5 py-0.5 rounded bg-muted">{t("esc", "Esc")}</kbd>
                <span>{t("close", "Close")}</span>
              </div>
            </div>
          </CommandPrimitive>
        </DialogContent>
      </Dialog>
    </>);

};

export default GlobalSearch;